import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Avatar,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Stack,
  CircularProgress,
} from '@mui/material';
import {
  TrendingUp,
  ShoppingCart,
  People,
  Inventory,
  AttachMoney,
  Notifications,
  ArrowUpward,
  ArrowDownward,
  ShoppingCartCheckout,
  BarChart,
  Person,
  VideoLibrary,
  Store,
} from '@mui/icons-material';
import { supabase } from '../../supabaseClient';
import { useDarkMode, getClozedStyles } from '../../hooks/useDarkMode';

interface DashboardStats {
  totalUsers: number;
  totalSales: number;
  totalReels: number;
  totalProducts: number;
  totalOrders: number;
  userGrowthRate: string;
  avgOrderValue: string;
  topReel: { title: string; views: number } | null;
}

interface RecentActivity {
  id: string;
  user: string;
  action: string;
  time: string;
  type: 'order' | 'product' | 'reel' | 'seller';
}

export default function AdminDashboard() {
  const { t } = useTranslation();
  const isDarkMode = useDarkMode();
  const styles = getClozedStyles(isDarkMode);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    totalSales: 0,
    totalReels: 0,
    totalProducts: 0,
    totalOrders: 0,
    userGrowthRate: '0%',
    avgOrderValue: '0원',
    topReel: null,
  });
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // 1. Total Users
      const { count: userCount } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      // 2. Total Sales (sum of all confirmed orders)
      const { data: ordersData } = await supabase
        .from('orders')
        .select('payment_amount')
        .eq('payment_status', 'paid');

      const totalSales = ordersData?.reduce((sum, order) => sum + (order.payment_amount || 0), 0) || 0;

      // 3. Total Reels
      const { count: reelsCount } = await supabase
        .from('contents')
        .select('*', { count: 'exact', head: true });

      // 4. Total Products
      const { count: productsCount } = await supabase
        .from('products')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');

      // 5. Total Orders
      const { count: ordersCount } = await supabase
        .from('orders')
        .select('*', { count: 'exact', head: true });

      // 6. User Growth Rate (last 30 days vs previous 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      const sixtyDaysAgo = new Date();
      sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

      const { count: recentUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', thirtyDaysAgo.toISOString());

      const { count: previousUsers } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', sixtyDaysAgo.toISOString())
        .lt('created_at', thirtyDaysAgo.toISOString());

      const growthRate = previousUsers && previousUsers > 0
        ? (((recentUsers || 0) - previousUsers) / previousUsers * 100)
        : 0;

      // 7. Average Order Value
      const avgOrderValue = ordersCount && ordersCount > 0
        ? Math.round(totalSales / ordersCount)
        : 0;

      // 8. Top Reel by views
      const { data: topReelData } = await supabase
        .from('contents')
        .select('description, view_count')
        .order('view_count', { ascending: false })
        .limit(1)
        .single();

      // 9. Recent Activity (last 10 activities)
      const activities: RecentActivity[] = [];

      // Recent orders
      const { data: recentOrders } = await supabase
        .from('orders')
        .select('id, created_at, user_id, profiles(nick_name)')
        .order('created_at', { ascending: false })
        .limit(5);

      recentOrders?.forEach(order => {
        activities.push({
          id: order.id,
          user: (order.profiles as any)?.nick_name || t('dashboard.unknown'),
          action: t('dashboard.orderCompleted'),
          time: getTimeAgo(order.created_at),
          type: 'order',
        });
      });

      // Recent products
      const { data: recentProducts } = await supabase
        .from('products')
        .select('product_id, created_at, product_name, seller_id, seller_profile(user_id, profiles(nick_name))')
        .order('created_at', { ascending: false })
        .limit(5);

      recentProducts?.forEach(product => {
        const sellerNickname = (product.seller_profile as any)?.profiles?.nick_name || t('dashboard.unknown');
        activities.push({
          id: product.product_id,
          user: sellerNickname,
          action: t('dashboard.productRegistered', { name: product.product_name }),
          time: getTimeAgo(product.created_at),
          type: 'product',
        });
      });

      // Sort by time and limit to 10
      activities.sort((a, b) => {
        return new Date(b.time).getTime() - new Date(a.time).getTime();
      });

      setStats({
        totalUsers: userCount || 0,
        totalSales,
        totalReels: reelsCount || 0,
        totalProducts: productsCount || 0,
        totalOrders: ordersCount || 0,
        userGrowthRate: `${growthRate > 0 ? '+' : ''}${growthRate.toFixed(1)}%`,
        avgOrderValue: `${avgOrderValue.toLocaleString()}${t('common.currencyUnit')}`,
        topReel: topReelData ? {
          title: topReelData.description || t('dashboard.noTitle'),
          views: topReelData.view_count || 0,
        } : null,
      });

      setRecentActivity(activities.slice(0, 10));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}분 전`;
    if (diffHours < 24) return `${diffHours}시간 전`;
    return `${diffDays}일 전`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <Box sx={{
      ...styles.pageContainer,
      p: 3,
      minHeight: '100vh'
    }}>
      <Typography
        variant="h4"
        sx={{
          ...styles.title,
          fontWeight: 600,
          mb: 3
        }}
      >
        {t('nav.dashboard')}
      </Typography>
      <Grid container spacing={3}>
        {/* 통계 카드 */}
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            p: 3,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: '#dbeafe',
                color: '#3b82f6',
                width: 56,
                height: 56
              }}>
                <People fontSize="large" />
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#1f2937'
                  }}
                >
                  {stats.totalUsers.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280'
                  }}
                >
                  {t('dashboard.totalUsers')}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            p: 3,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: '#d1fae5',
                color: '#10b981',
                width: 56,
                height: 56
              }}>
                <AttachMoney fontSize="large" />
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#1f2937'
                  }}
                >
                  {stats.totalSales.toLocaleString()}원
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280'
                  }}
                >
                  {t('dashboard.totalSales')}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            p: 3,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: '#fef3c7',
                color: '#f59e0b',
                width: 56,
                height: 56
              }}>
                <VideoLibrary fontSize="large" />
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#1f2937'
                  }}
                >
                  {stats.totalReels.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280'
                  }}
                >
                  {t('dashboard.totalReels')}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            p: 3,
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              transform: 'translateY(-1px)'
            }
          }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <Avatar sx={{
                bgcolor: '#e0e7ff',
                color: '#6366f1',
                width: 56,
                height: 56
              }}>
                <Store fontSize="large" />
              </Avatar>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 700,
                    color: '#1f2937'
                  }}
                >
                  {stats.totalProducts.toLocaleString()}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280'
                  }}
                >
                  {t('dashboard.activeProducts')}
                </Typography>
              </Box>
            </Stack>
          </Card>
        </Grid>
      </Grid>

      {/* Quick Stats */}
      <Grid container spacing={3} mt={1} alignItems="flex-start">
        <Grid item xs={12} md={12}>
          <Paper sx={{
            borderRadius: 2,
            p: 3,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb',
            minHeight: 220
          }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                mb: 3,
                color: '#1f2937'
              }}
            >
              {t('dashboard.keyMetrics')}
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: '#f3f4f6'
                  }}>
                    <ArrowUpward color="success" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: '#1f2937'
                      }}
                    >
                      {t('dashboard.userGrowthRate')}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6b7280'
                      }}
                    >
                      {stats.userGrowthRate} ({t('dashboard.last30Days')})
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: '#f3f4f6'
                  }}>
                    <TrendingUp color="primary" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: '#1f2937'
                      }}
                    >
                      평균 주문 금액
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6b7280'
                      }}
                    >
                      {stats.avgOrderValue}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: '#f3f4f6'
                  }}>
                    <BarChart color="warning" fontSize="small" />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: 15,
                        color: '#1f2937'
                      }}
                    >
                      최다 조회 릴스
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: '#6b7280'
                      }}
                    >
                      {stats.topReel ? `${stats.topReel.views.toLocaleString()}회 조회` : '데이터 없음'}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>

      {/* 최근 활동 */}
      <Box mt={4}>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            mb: 3,
            color: '#1f2937'
          }}
        >
          최근 활동
        </Typography>
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            backgroundColor: '#ffffff',
            border: '1px solid #e5e7eb'
          }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  사용자
                </TableCell>
                <TableCell sx={{
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  활동
                </TableCell>
                <TableCell sx={{
                  color: '#6b7280',
                  fontWeight: 600,
                  fontSize: '12px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  borderBottom: '1px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  시간
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {recentActivity.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      최근 활동이 없습니다
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                recentActivity.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell sx={{ borderBottom: '1px solid #f3f4f6' }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar sx={{
                          width: 32,
                          height: 32,
                          backgroundColor: row.type === 'order' ? '#dbeafe' :
                                           row.type === 'product' ? '#d1fae5' :
                                           row.type === 'reel' ? '#fef3c7' : '#e0e7ff',
                          color: row.type === 'order' ? '#3b82f6' :
                                 row.type === 'product' ? '#10b981' :
                                 row.type === 'reel' ? '#f59e0b' : '#6366f1',
                          fontWeight: 600,
                          fontSize: '14px'
                        }}>
                          {row.user[0]}
                        </Avatar>
                        <Typography
                          sx={{
                            fontWeight: 500,
                            color: '#1f2937',
                            fontSize: '14px'
                          }}
                        >
                          {row.user}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #f3f4f6' }}>
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: '#1f2937',
                          fontSize: '14px'
                        }}
                      >
                        {row.action}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ borderBottom: '1px solid #f3f4f6' }}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#6b7280',
                          fontSize: '14px'
                        }}
                      >
                        {row.time}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
} 